import { Box, VStack, Text, HStack, Spinner, DataList, Flex } from "@chakra-ui/react"
import { FaClock, FaDatabase, FaTruck } from "react-icons/fa6"
import { Chart, useChart } from "@chakra-ui/charts"
import { Line, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts"
import { useMemo, useState } from "react"
import { useForecastContext } from "./ForecastProvider"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useIsAdmin } from "@hydrologiq/shared/providers/helpers/useUserRole"
import ForecastComparisonPanel from "./ForecastComparisonPanel"
import { ParsedDataPoint, aggregateComparisonData } from "@hydrologiq/shared/utils/csvParser"
import { ForecastRefuelData } from "@hydrologiq/gql-experience/graphql"
import { DateTime, days, hours, minutes, TimeZone } from "timezonecomplete"
import StatusBadge from "@hydrologiq/shared/components/StatusBadge"
import type { StatusType } from "@hydrologiq/shared/components/StatusBadge"

interface AssetForecastChartProps {
  entityType: "asset"
  assetId: string
  groupHourlyData?: boolean
}

interface InstallationForecastChartProps {
  entityType: "installation"
  installationId: string
  siteId?: string
  customerId?: string
  groupHourlyData?: boolean
}

type ForecastChartProps = AssetForecastChartProps | InstallationForecastChartProps

type ForecastView = "hourly" | "daily"

interface AccuracyMetrics {
  mae: number
  rmse: number
  mape: number
  overlappingPoints: number
}

interface CustomForecastTooltipPayload {
  date: string
  intervalStartDate?: string
  actualLevel: number | null
  predictedLevel: number | null
  comparisonLevel: number | null
  predictedConsumption: number | null
  startLevel: number | null
  minWeight: number
  interval: string
  hour: number | null
  isRefuel: boolean
  refuelData?: {
    weightBefore?: number | null
    weightStart?: number | null
    weightEnd?: number | null
    amount?: number | null
    hasOrder?: boolean
    order?: {
      id: string
      name: string
      status: string
      quantity: number
      quantityCollected?: number | null
      collectionReplacement?: boolean | null
    } | null
  } | null
  isHistorical?: boolean
}

interface CustomForecastTooltipProps {
  active?: boolean
  payload?: { payload: CustomForecastTooltipPayload }[]
  label?: string
}

const PredictedLevelColor = "#8f2eff"
const ActualLevelColor = "#39aed2"
const ComparisonLevelColor = "#f56500"
const OrderRefuelColor = "#2563eb"      // blue — order-backed refuel
const SuggestionRefuelColor = "#9ca3af" // grey — suggested refuel
const ActualRefuelColor = "#16a34a"     // green — historical actual refuel

function formatIntervalTitle(
  intervalStartDate: string | undefined,
  endDate: string,
  isDaily: boolean,
): string {
  if (isDaily) {
    return new DateTime(endDate).format("MMM dd, yyyy")
  }
  if (!intervalStartDate || intervalStartDate === endDate) {
    return new DateTime(endDate).format("MMM dd, yyyy, HH:mm")
  }
  const start = new DateTime(intervalStartDate)
  const end = new DateTime(endDate).sub(minutes(1))
  const startStr = start.format("MMM dd, yyyy, HH:mm")
  const endStr = end.format("MMM dd, yyyy, HH:mm")
  if (start.year() === end.year() && start.month() === end.month() && start.day() === end.day()) {
    return `${startStr} - ${end.format("HH:mm")}`
  }
  return `${startStr} - ${endStr}`
}

function formatLevel(level: number): number {
  return Math.floor(level)
}

// Custom tooltip component that displays refuelling data
function CustomForecastTooltip(props: CustomForecastTooltipProps) {
  const { active, payload } = props

  if (!active || !payload || payload.length === 0) {
    return null
  }

  // Get the data point from the first payload item
  const dataPoint = payload[0]?.payload
  const title = formatIntervalTitle(
    dataPoint?.intervalStartDate,
    dataPoint?.date ?? "",
    dataPoint?.interval === "day",
  )
  const refuelData = dataPoint?.refuelData
  const predictedLevel = dataPoint?.predictedLevel
  const actualLevel = dataPoint?.actualLevel
  const comparisonLevel = dataPoint?.comparisonLevel


  const startLevel = dataPoint?.startLevel
  const predictedConsumption = dataPoint?.predictedConsumption
  const isForecastPoint = dataPoint?.isHistorical === false

  const baseData: { label: string; value: string }[] = []
  const addItem = (label: string, value: number | string | null, unit: string | undefined, data: { label: string; value: string }[]) => {
    if (value !== null) {
      data.push({ label, value: `${typeof value === "number" ? formatLevel(value) : value}${unit ? `${unit}` : ''}` })
    }
  }
  addItem("Actual weight", actualLevel, "kg", baseData)
  if (isForecastPoint) {
    addItem("Start", startLevel, "kg", baseData)
  }
  if (!actualLevel && !isForecastPoint) {
    addItem("Predicted weight", predictedLevel, "kg", baseData)
  }
  addItem("Comparison weight", comparisonLevel, "kg", baseData)

  // Refuel data — show breakdown for order-backed refuels
  if (refuelData) {
    if (refuelData.order) {
      const beforeLevel = refuelData.weightBefore ?? refuelData.weightStart ?? 0
      const collected = refuelData.order.quantityCollected ?? 0
      if (refuelData.order.collectionReplacement) {
        // Collection replacement: all remaining collected, then delivery
        addItem("Collection", `${formatLevel(beforeLevel)}kg → 0kg (-${formatLevel(beforeLevel)}kg)`, undefined, baseData)
        addItem("Delivery", `0kg → ${formatLevel(refuelData?.weightEnd ?? 0)}kg (+${formatLevel(refuelData.order.quantity)}kg)`, undefined, baseData)
      } else {
        // Non-replacement: show collection (if any) then delivery
        const afterCollection = beforeLevel - collected
        if (collected > 0) {
          addItem("Collection", `${formatLevel(beforeLevel)}kg → ${formatLevel(afterCollection)}kg (-${formatLevel(collected)}kg)`, undefined, baseData)
        }
        addItem("Delivery", `${formatLevel(afterCollection)}kg → ${formatLevel(refuelData?.weightEnd ?? 0)}kg (+${formatLevel(refuelData.order.quantity)}kg)`, undefined, baseData)
      }
    } else {
      addItem("Refuelled", `${formatLevel(refuelData?.amount ?? 0)}kg (${formatLevel(refuelData?.weightStart ?? 0)}kg → ${formatLevel(refuelData?.weightEnd ?? 0)}kg)`, undefined, baseData)
    }
  }

  if (isForecastPoint) {
    addItem("End", predictedLevel, "kg", baseData)
    addItem("Consumption", predictedConsumption, "kg", baseData)
  }

  return (
    <Box
      bg="bg"
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      p={3}
      boxShadow="lg"
    >
      <VStack align="stretch" gap={1} fontSize="xs">
        <Text fontWeight="semibold">
          {title}
        </Text>

        <DataList.Root orientation="horizontal" gap={0}>
          {baseData.map((item) => (
            <DataList.Item key={item.label}>
              <DataList.ItemLabel>{item.label}</DataList.ItemLabel>
              <DataList.ItemValue>{item.value}</DataList.ItemValue>
            </DataList.Item>
          ))}
        </DataList.Root>

        {/* Order breakdown section */}
        {refuelData?.order && (
          <VStack align="stretch" gap={0.5} mt={1} pt={1} borderTopWidth="1px" borderColor="border">
            <HStack gap={1}>
              <FaTruck size={10} />
              <Text fontWeight="semibold" fontSize="xs">{refuelData.order.name}</Text>
              <StatusBadge status={mapOrderStatusToBadge(refuelData.order.status)} />
            </HStack>
            <VStack align="stretch" gap={0} pl={4}>
              {refuelData.order.collectionReplacement ? (
                <>
                  <Text fontSize="xs" color="fg.muted">
                    Full replacement (all remaining collected)
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    +{formatLevel(refuelData.order.quantity)}kg delivered
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold" borderTopWidth="1px" borderColor="border.muted" mt={0.5} pt={0.5}>
                    +{formatLevel(refuelData.order.quantity)}kg net
                  </Text>
                </>
              ) : (
                <>
                  {(refuelData.order.quantityCollected ?? 0) > 0 && (
                    <Text fontSize="xs" color="fg.muted">
                      -{formatLevel(refuelData.order.quantityCollected!)}kg collected
                    </Text>
                  )}
                  <Text fontSize="xs" color="fg.muted">
                    +{formatLevel(refuelData.order.quantity)}kg delivered
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold" borderTopWidth="1px" borderColor="border.muted" mt={0.5} pt={0.5}>
                    +{formatLevel(refuelData.order.quantity - (refuelData.order.quantityCollected ?? 0))}kg net
                  </Text>
                </>
              )}
            </VStack>
          </VStack>
        )}
      </VStack>
    </Box>
  )
}

// Custom dot component to highlight refuel points
interface RefuelDotProps {
  cx?: number
  cy?: number
  payload?: any
  value?: number
  baseColor: string
}

function RefuelDot({ cx, cy, payload, baseColor }: RefuelDotProps) {
  if (!cx || !cy || !payload) {
    return null
  }

  // If this is a refuel point, render a larger highlighted dot with colour coding
  if (payload.isRefuel) {
    const hasOrder = payload.refuelData?.hasOrder ?? false
    const isHistorical = payload.isHistorical ?? false

    // Determine dot fill colour based on refuel type
    let dotColor: string
    if (hasOrder) {
      dotColor = OrderRefuelColor       // blue — order-backed
    } else if (isHistorical) {
      dotColor = ActualRefuelColor      // green — historical actual refuel
    } else {
      dotColor = SuggestionRefuelColor  // grey — suggestion
    }

    return (
      <g>
        {/* Outer ring */}
        <circle
          cx={cx}
          cy={cy}
          r={7}
          fill="none"
          stroke={dotColor}
          strokeWidth={2}
        />
        {/* Inner dot — coloured by refuel type */}
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={dotColor}
          stroke="#fff"
          strokeWidth={1.5}
        />
      </g>
    )
  }

  // For non-refuel points, render a normal dot
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={baseColor}
    />
  )
}

// Helper function to format the check back time.
// Computes remaining time from requiredHours - hoursSinceRefuel.
function formatCheckBackTime(remainingHours: number): string {
  const diffMs = remainingHours * 60 * 60 * 1000

  if (diffMs <= 0) {
    return "now"
  }

  const diffMinutes = Math.ceil(diffMs / (1000 * 60))
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60))
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'}`
  } else {
    return `${diffDays} day${diffDays === 1 ? '' : 's'}`
  }
}

// Group hourly data into 8-hour intervals, taking the last value from each period
// Rounds up the first data point to the nearest 8-hour interval boundary and includes refuelling data if present
function groupHourlyDataIntoIntervals<T extends { date: string; intervalStartDate?: string; isRefuel?: boolean; refuelData?: ForecastRefuelData | null; cutOffDate?: string; predictedConsumption?: number | null; predictedLevel?: number | null; startLevel?: number | null, endLevel?: number | null }>(
  dataPoints: T[],
  cutOffDate?: string
): T[] {
  if (dataPoints.length === 0) {
    return []
  }

  // Get the first data point's timestamp using DateTime with UTC timezone
  const utcTimeZone = TimeZone.utc()
  const firstPointDateTime = new DateTime(dataPoints[0].date, utcTimeZone)
  const FOUR_HOURS_IN_SECONDS = 8 * 60 * 60 // 4 hours in seconds

  // Round up to the next 4-hour interval boundary
  // 4-hour intervals: 00:00, 04:00, 08:00, 12:00, 16:00, 20:00
  // Always rounds forward (e.g., 15:00 -> 16:00, 16:00 -> 20:00)
  const hours = firstPointDateTime.hour()

  // Calculate which 4-hour interval this hour falls into, then always round up to the next interval
  const currentIntervalHour = Math.floor(hours / 4) * 4
  const nextIntervalHour = currentIntervalHour + 4

  // Always round up to the next 4-hour boundary (e.g., 15:00 -> 16:00, 16:00 -> 20:00)
  const roundedHour = nextIntervalHour >= 24 ? 0 : nextIntervalHour

  // Create a new DateTime at the rounded interval boundary in UTC
  let roundedDateTime = new DateTime(
    firstPointDateTime.year(),
    firstPointDateTime.month(),
    firstPointDateTime.day(),
    roundedHour,
    0,
    0,
    0,
    utcTimeZone
  )

  // If we rounded up past midnight, move to the next day
  if (nextIntervalHour >= 24) {
    const newDate = roundedDateTime.add(days(1))
    roundedDateTime = new DateTime(
      newDate.year(),
      newDate.month(),
      newDate.day(),
      0,
      0,
      0,
      0,
      utcTimeZone
    )
  }

  const firstPointTimeRounded = roundedDateTime.unixUtcMillis() / 1000

  const grouped: T[] = []
  let currentGroup: T[] = []
  let currentIntervalStart = firstPointTimeRounded

  for (let i = 0; i < dataPoints.length; i++) {
    const point = dataPoints[i]
    const pointDateTime = new DateTime(point.date, utcTimeZone)
    const pointTime = pointDateTime.unixUtcMillis() / 1000

    // Calculate which interval this point belongs to (starting from rounded first point)
    // Use Math.floor so points before the rounded time are included in the first interval
    const intervalIndex = Math.floor((pointTime - firstPointTimeRounded) / FOUR_HOURS_IN_SECONDS)
    const intervalStart = firstPointTimeRounded + (intervalIndex * FOUR_HOURS_IN_SECONDS)

    // If we've moved to a new interval, finalize the previous group
    if (intervalStart !== currentIntervalStart) {
      // Finalize previous group if it has points
      if (currentGroup.length > 0) {
        const firstPoint = currentGroup[0]
        // Take the last point in the group
        const lastPoint = currentGroup[currentGroup.length - 1]

        // Check if any point in the group has refuelling data
        const refuelPoint = currentGroup.find(p => p.isRefuel && p.refuelData)

        // Calculate the end of the interval window (currentIntervalStart + 8 hours)
        const intervalEnd = currentIntervalStart + FOUR_HOURS_IN_SECONDS
        const intervalEndDateTime = new DateTime(intervalEnd * 1000, utcTimeZone)
        const intervalEndDate = intervalEndDateTime.toIsoString()
        const intervalStartDateTime = new DateTime(currentIntervalStart * 1000, utcTimeZone)
        const intervalStartDate = intervalStartDateTime.toIsoString()

        // Sum consumption across all points in the group
        const summedConsumption = currentGroup.reduce(
          (sum, p) => sum + (p.predictedConsumption ?? 0),
          0
        )

        // Create the grouped point with date set to the end of the interval window
        const groupedPoint: T = {
          ...lastPoint,
          date: intervalEndDate,
          intervalStartDate: intervalStartDate as T["intervalStartDate"],
          predictedConsumption: summedConsumption as T["predictedConsumption"],
          startLevel: firstPoint.startLevel,
          endLevel: lastPoint.endLevel,
          // If there's a refuel point, use its refuel data (prefer the last one)
          isRefuel: refuelPoint ? true : (lastPoint.isRefuel || false),
          refuelData: refuelPoint?.refuelData || lastPoint.refuelData || null,
        }

        grouped.push(groupedPoint)
        currentGroup = []
      }
      // Update current interval start for the new interval
      currentIntervalStart = intervalStart
    }

    // Add point to current group
    currentGroup.push(point)
  }

  // Don't forget the last group
  if (currentGroup.length > 0) {
    const firstPoint = currentGroup[0]
    const lastPoint = currentGroup[currentGroup.length - 1]
    const refuelPoint = currentGroup.find(p => p.isRefuel && p.refuelData)

    // Calculate the end of the interval window (currentIntervalStart + 8 hours)
    const intervalEnd = currentIntervalStart + FOUR_HOURS_IN_SECONDS
    const intervalEndDateTime = new DateTime(intervalEnd * 1000, utcTimeZone)
    const intervalEndDate = intervalEndDateTime.toIsoString()
    const intervalStartDateTime = new DateTime(currentIntervalStart * 1000, utcTimeZone)
    const intervalStartDate = intervalStartDateTime.toIsoString()

    // Sum consumption across all points in the group
    const summedConsumption = currentGroup.reduce(
      (sum, p) => sum + (p.predictedConsumption ?? 0),
      0
    )

    const groupedPoint: T = {
      ...lastPoint,
      date: intervalEndDate,
      intervalStartDate: intervalStartDate as T["intervalStartDate"],
      predictedConsumption: summedConsumption as T["predictedConsumption"],
      startLevel:
        firstPoint.startLevel,
      endLevel:
        lastPoint.endLevel,
      isRefuel: refuelPoint ? true : (lastPoint.isRefuel || false),
      refuelData: refuelPoint?.refuelData || lastPoint.refuelData || null,
    }

    if (cutOffDate && new DateTime(intervalEndDate).greaterThan(new DateTime(cutOffDate))) {
      return grouped
    } else {
      grouped.push(groupedPoint)
    }
  }


  return grouped
}

// Calculate accuracy metrics by comparing forecast predictions with actual comparison data
function calculateAccuracyMetrics(forecastPoints: any[], comparisonData: ParsedDataPoint[]): AccuracyMetrics | null {
  const overlaps: { predicted: number; actual: number }[] = []

  // Since comparison data is now aggregated and aligned, we can match by exact timestamps
  forecastPoints.forEach(fp => {
    const forecastTime = new Date(fp.date).getTime()

    // Find exact timestamp match (aggregated data should have matching timestamps)
    const matchingComparison = comparisonData.find(cp => {
      const comparisonTime = new Date(cp.date).getTime()
      return comparisonTime === forecastTime
    })

    if (matchingComparison) {
      overlaps.push({
        predicted: fp.predictedLevel,
        actual: matchingComparison.weight
      })
    }
  })

  if (overlaps.length === 0) {
    return { mae: 0, rmse: 0, mape: 0, overlappingPoints: 0 }
  }

  // Calculate MAE (Mean Absolute Error)
  const mae = overlaps.reduce((sum, { predicted, actual }) =>
    sum + Math.abs(predicted - actual), 0) / overlaps.length

  // Calculate RMSE (Root Mean Square Error)
  const mse = overlaps.reduce((sum, { predicted, actual }) =>
    sum + Math.pow(predicted - actual, 2), 0) / overlaps.length
  const rmse = Math.sqrt(mse)

  // Calculate MAPE (Mean Absolute Percentage Error)
  const mape = overlaps.reduce((sum, { predicted, actual }) => {
    if (actual === 0) return sum // Avoid division by zero
    return sum + Math.abs((predicted - actual) / actual) * 100
  }, 0) / overlaps.length

  return {
    mae,
    rmse,
    mape,
    overlappingPoints: overlaps.length
  }
}

// Map order status string to StatusBadge type
function mapOrderStatusToBadge(status: string): StatusType {
  const lower = status.toLowerCase()
  if (lower === "started") return "in_progress"
  if (lower === "completed") return "completed"
  if (lower === "billed") return "billed"
  return "scheduled"
}

export default function ForecastChart(props: ForecastChartProps) {
  const [view, setView] = useState<ForecastView>("hourly")
  const [comparisonData, setComparisonData] = useState<ParsedDataPoint[] | null>(null)

  // Extract groupHourlyData prop (optional, defaults to false)
  const groupHourlyData = props.groupHourlyData ?? true

  // Access control for debug features
  const isAdmin = useIsAdmin()
  const debugEnabled = useGetFeature("asset_forecast_debug") // Keep same feature flag for now
  const showDebugFeatures = isAdmin && debugEnabled

  // Use shared forecast data from provider
  const {
    hourlyForecast,
    dailyForecast,
    hourlyForecastUnavailable,
    dailyForecastUnavailable,
    hourlyLoading,
    dailyLoading,
    refreshing,
  } = useForecastContext()

  // Determine which forecast to use based on view
  const forecast = useMemo(() => view === "hourly" ? hourlyForecast : dailyForecast, [view, hourlyForecast, dailyForecast])
  const forecastUnavailable = useMemo(() => view === "hourly" ? hourlyForecastUnavailable : dailyForecastUnavailable, [view, hourlyForecastUnavailable, dailyForecastUnavailable])
  const loading = useMemo(() => view === "hourly" ? hourlyLoading : dailyLoading, [view, hourlyLoading, dailyLoading])

  // Check if we have hourly data available
  const hasHourlyData = !!hourlyForecast


  // Transform forecast data for chart
  const forecastData = useMemo(() => {
    if (!forecast) {
      return []
    }

    const intervalDurationHours = view === "hourly" ? (groupHourlyData ? 8 : 1) : 24

    let transformed = forecast.forecast.map((point) => {
      const predictedLevel = Math.round(point.predictedLevel * 10) / 10
      const predictedConsumption = Math.round(point.predictedConsumption * 100) / 100
      let startLevel = Math.round(point.predictedLevel * 10) / 10

      if (point.isRefuel && point.refuelData?.weightStart != null) {
        // For order-backed refuels, use weightBefore (pre-event level) as startLevel
        if (point.refuelData?.hasOrder && point.refuelData?.weightBefore != null) {
          startLevel = Math.round(point.refuelData.weightBefore * 10) / 10
        } else {
          startLevel = Math.round(point.refuelData?.weightStart * 10) / 10
          if (point.predictedConsumptionLower != null) startLevel += Math.round(point.predictedConsumptionLower * 10) / 10
        }
      } else if (point.predictedConsumption !== null) startLevel += Math.round(point.predictedConsumption * 10) / 10
      let endLevel = predictedLevel

      const endDateTime = new DateTime(point.date)
      const intervalStartDate = endDateTime.add(hours(-intervalDurationHours)).toIsoString()
      return {
        date: point.date,
        intervalStartDate,
        actualLevel: null,
        predictedLevel,
        comparisonLevel: null,
        predictedConsumption,
        endLevel,
        startLevel,
        minWeight: forecast.minWeight,
        interval: point.interval || (view === "hourly" ? "hour" : "day"),
        hour: point.hour,
        isRefuel: point.isRefuel,
        refuelData: point.refuelData,
        isHistorical: false,
      }
    })


    // Apply grouping if enabled and view is hourly
    if (groupHourlyData && view === "hourly") {
      return groupHourlyDataIntoIntervals(transformed)
    }

    return transformed
  }, [forecast, view, groupHourlyData])

  // Transform historical data for chart
  const historicalData = useMemo(() => {
    if (!forecast) {
      return []
    }

    const intervalDurationHours = view === "hourly" ? (groupHourlyData ? 8 : 1) : 24

    let transformed = forecast.historicalData.map((point) => {
      const endDateTime = new DateTime(point.date)
      const intervalStartDate = endDateTime.add(hours(-intervalDurationHours)).toIsoString()
      return {
        date: point.date,
        intervalStartDate,
        actualLevel: Math.floor(point.weight * 10) / 10,
        predictedLevel: null,
        comparisonLevel: null,
        predictedConsumption: null,
        startLevel: null,
        endLevel: null,
        minWeight: forecast.minWeight,
        hour: point.hour, // Include hour for hourly data
        isRefuel: point.isRefuel,
        refuelData: point.refuelData,
        isHistorical: true,
        interval: (view === "hourly" ? "hour" : "day"),
      }
    })


    // Apply grouping if enabled and view is hourly
    if (groupHourlyData && view === "hourly") {
      transformed = groupHourlyDataIntoIntervals(transformed, forecast.historicalData[forecast.historicalData.length - 1]?.date)
    }

    if (transformed.length > 0) {
      (transformed[transformed.length - 1] as any)["predictedLevel"] = transformed[transformed.length - 1].actualLevel
    }

    return transformed
  }, [forecast, view, groupHourlyData])

  // Merge comparison data into existing chart data points instead of adding new ones
  const chartData = useMemo(() => {
    if (historicalData && forecastData && forecastData[0]) {
      let lastHistoricalPoint = historicalData[historicalData.length - 1]
      // @ts-ignore
      lastHistoricalPoint.endLevel = lastHistoricalPoint.actualLevel
    }
    // Start with combined historical and forecast data
    let baseChartData = [...historicalData, ...forecastData]




    // Use startLevel ?? predictedLevel for the predicted line (bridge point has predictedLevel but not startLevel)
    const withStartLevelFallback = baseChartData.map(chartPoint => ({
      ...chartPoint
    }))

    if (!comparisonData || !forecast) {
      return withStartLevelFallback
    }

    // Aggregate comparison data to match forecast intervals only (not historical)
    const aggregatedData = aggregateComparisonData(
      comparisonData,
      forecastData, // Only match against forecast data points
      view === "hourly" ? 1 : 24 // Use interval for hourly, 24h for daily
    )

    // Create a map of comparison data by date for fast lookup
    const comparisonMap = new Map(
      aggregatedData.map(point => [point.date, formatLevel(point.weight)])
    )

    // Merge comparison values into existing chart data points
    // Only add comparison data for forecast points (where we have predictedLevel)
    return withStartLevelFallback.map(chartPoint => ({
      ...chartPoint,
      comparisonLevel: chartPoint.predictedLevel !== null
        ? (comparisonMap.get(chartPoint.date) || null)
        : null // Don't show comparison for historical data
    }))
  }, [historicalData, forecastData, comparisonData, forecast, view])

  // Calculate accuracy metrics using aggregated comparison data (only against forecast, not historical)
  const accuracyMetrics = forecast && comparisonData ? (() => {
    // For accuracy metrics, we only want to compare against forecast data (not historical)
    const forecastOnlyAggregated = aggregateComparisonData(
      comparisonData,
      forecastData, // Only use forecast data for accuracy calculation
      view === "hourly" ? 1 : 24
    )
    return calculateAccuracyMetrics(forecast.forecast, forecastOnlyAggregated)
  })() : null

  // Chart series configuration - MUST be called before any conditional returns
  const baseSeries = [
    { name: "endLevel" as const, color: "blue.500", label: "Predicted Level (kg)" },
    { name: "actualLevel" as const, color: "green.500", label: "Actual Level (kg)" },

  ]

  const chartSeries = comparisonData
    ? [...baseSeries, { name: "comparisonLevel" as const, color: "orange.500", label: "Comparison Data (kg)" }]
    : baseSeries

  // Create chart configuration
  const chart = useChart({
    data: chartData,
    series: chartSeries,
  })

  // Conditional returns AFTER all hooks
  if (loading) {
    return (
      <Box
        height="500px"
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderWidth="1px"
        borderColor="border"
      >
        <VStack gap={4}>
          <Spinner size="xl" />
          <Text fontSize="lg" fontWeight="semibold" color="fg.muted">
            Predicting the future...
          </Text>
          <Text fontSize="sm" color="fg.subtle">
            Analysing consumption patterns
          </Text>
        </VStack>
      </Box>
    )
  }

  if (!forecast || forecast.forecast.length === 0) {
    // Check if we have structured unavailable response
    if (forecastUnavailable) {
      const remainingHours = Math.max(0, forecastUnavailable.requiredHours - forecastUnavailable.hoursSinceRefuel)
      const timeUntilAvailable = formatCheckBackTime(remainingHours)
      const requiredTime = forecastUnavailable.hasProfile ? "10 minutes" : "24 hours"
      const supported = forecastUnavailable.reason.includes("not currently supported") ? false : true

      return (
        <Box
          p={8}
          textAlign="center"
          borderWidth="1px"
          borderColor="background"
          borderRadius="lg"
          bg="background"
        >
          <VStack gap={4}>
            <HStack gap={3} color="white" _dark={{ color: "orange.400" }}>
              <Box
                p={3}
                borderRadius="full"
                bg="active"
              >
                <FaClock size={20} />
              </Box>
              <VStack gap={1} align="start">
                <Text fontSize="lg" fontWeight="semibold" color="fg">
                  {supported ? "Collecting forecast data..." : "Forecast not currently supported for this asset type"}
                </Text>
                <Text fontSize="sm" color="primary">
                  Please check back in {timeUntilAvailable}
                </Text>

              </VStack>
            </HStack>
            {supported && (<HStack gap={2} justify="center" color="primary">
              <FaDatabase size={14} />
              <Text fontWeight="medium" fontSize="sm">
                Requires at least {requiredTime} of data since the last refuel
              </Text>
            </HStack>)}
          </VStack >
        </Box >
      )
    }

    // Fallback for generic no-data case
    return (
      <Box p={8} textAlign="center" borderWidth="1px" borderColor="border" borderRadius="md">
        <VStack gap={2}>
          <Text fontSize="md" fontWeight="semibold" color="fg.muted">
            No forecast data available
          </Text>
          <Text fontSize="sm" color="fg.subtle">
            Forecast data is not available for this entity
          </Text>
        </VStack>
      </Box>
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      {/* Chart Title and View Toggle */}
      <Box>
        <HStack justify="space-between" align="start" mb={2}>
          <Box>
            <Text fontSize="lg" fontWeight="semibold">
              Hydrogen Level - Historical & Forecast
            </Text>
            <Text fontSize="sm" color="fg">
              {view === "hourly"
                ? (forecast.historicalData.length > 0
                  ? "Historical data (last 7 days) since previous refuel | 5-day hourly forecast | 8 hour intervals"
                  : "5-day hourly forecast")
                : (forecast.historicalData.length > 0
                  ? "Historical data (last 7 days) since previous refuel | 30-day daily forecast | 24 hour intervals"
                  : "30-day daily forecast")
              }
            </Text>
          </Box>

          {/* View Toggle Buttons */}
          {hasHourlyData && (
            <HStack gap={2} >
              <SegmentedControl
                items={[
                  { value: "hourly", label: "Next 5 days" },
                  { value: "daily", label: "Next 30 days" },
                ]}
                value={view}
                onValueChange={(value) => setView(value.value as ForecastView)}
              />
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Legend */}
      <HStack gap={4} px={1}>
        <HStack gap={1.5}>
          <Box w="12px" h="12px" borderRadius="full" bg={OrderRefuelColor} />
          <Text fontSize="xs" color="fg.muted">Order</Text>
        </HStack>
        <HStack gap={1.5}>
          <Box w="12px" h="12px" borderRadius="full" bg={SuggestionRefuelColor} />
          <Text fontSize="xs" color="fg.muted">Suggestion</Text>
        </HStack>
        <HStack gap={1.5}>
          <Box w="12px" h="12px" borderRadius="full" bg={ActualRefuelColor} />
          <Text fontSize="xs" color="fg.muted">Actual Refuel</Text>
        </HStack>
        <HStack gap={1.5}>
          <Box w="10px" h="3px" bg={PredictedLevelColor} borderRadius="sm" />
          <Text fontSize="xs" color="fg.muted">Predicted</Text>
        </HStack>
        <HStack gap={1.5}>
          <Box w="10px" h="3px" bg={ActualLevelColor} borderRadius="sm" />
          <Text fontSize="xs" color="fg.muted">Actual</Text>
        </HStack>
      </HStack>

      {/* Chart */}
      <Box borderWidth="1px" borderColor="border" borderRadius="md" p={4} bg="card" position="relative">
        {/* Refreshing overlay — matches calendar loading pattern */}
        {refreshing && (
          <Flex
            position="absolute"
            inset={0}
            zIndex={20}
            bg="rgba(255,255,255,0.6)"
            backdropFilter="blur(1px)"
            align="center"
            justify="center"
            direction="column"
            gap={3}
            borderRadius="md"
            pointerEvents="none"
          >
            <Spinner size="lg" color="blue.500" borderWidth="3px" />
            <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
              Updating forecast...
            </Text>
          </Flex>
        )}
        <Chart.Root height="400px" chart={chart}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chart.data}
            >
              <CartesianGrid stroke={chart.color("border.muted")} vertical={false} />
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey={chart.key("date")}
                tickFormatter={chart.formatDate({ year: "numeric", month: "short", day: "numeric" })}
                style={{ fontSize: "12px" }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${value} kg`}
                style={{ fontSize: "12px" }}
              />
              <Tooltip
                shared={true}
                cursor={{ stroke: chart.color("border"), strokeWidth: 1 }}
                animationDuration={0}
                labelFormatter={(value) => {
                  const date = new Date(value)
                  if (view === "hourly") {
                    return date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })
                  } else {
                    return date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  }
                }}
                content={<CustomForecastTooltip />}
              />

              {/* Min weight reference line */}
              <ReferenceLine
                y={forecast.minWeight}
                stroke={chart.color("#d2042d")}
                strokeDasharray="5 5"
                label={{
                  value: "Min Weight",
                  position: "left",
                  fill: chart.color("#d2042d"),
                  fontSize: 12,
                }}
              />

              {/* Predicted level line (shows start value for each interval) */}
              <Line
                type="monotone"
                isAnimationActive={false}
                dataKey={chart.key("endLevel")}
                stroke={chart.color(PredictedLevelColor)}
                strokeWidth={3}
                dot={<RefuelDot baseColor={chart.color(PredictedLevelColor)} />}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />
              {/* Actual level line (solid) */}
              <Line
                type="monotone"
                isAnimationActive={false}
                dataKey={chart.key("actualLevel")}
                stroke={chart.color(ActualLevelColor)}
                strokeWidth={3}
                dot={<RefuelDot baseColor={chart.color(ActualLevelColor)} />}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />




              {/* Comparison data line (dotted) */}
              {comparisonData && (
                <Line
                  type="monotone"
                  isAnimationActive={false}
                  dataKey={chart.key("comparisonLevel")}
                  stroke={chart.color(ComparisonLevelColor)}
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  dot={{ fill: chart.color(ComparisonLevelColor), r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Chart.Root>
      </Box>

      {/* Debug/Comparison Panel - only show for admins with debug feature enabled */}
      {showDebugFeatures && (
        <ForecastComparisonPanel
          comparisonData={comparisonData}
          onComparisonDataChange={setComparisonData}
          accuracyMetrics={accuracyMetrics}
        />
      )}
    </VStack>
  )
}